import React from "react";
import injectSheet from "react-jss";
import { v4 as uuid } from "uuid";
import { sortable } from "react-sortable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

import TourInformation from "../components/TourInformation";
import ModeSelect from "../components/ModeSelect";
import Button from "../components/Button";
import FloorplanSelector from "../components/FloorplanSelector";
import FloorplanItem from "../components/FloorplanItem";
import Floorplan from "../components/Floorplan";
import TopBar from "../components/refresh/TopBar";
import produce from "immer";

class FloorplanItemS extends React.Component {
  render() {
    return <li {...this.props}>{this.props.children}</li>;
  }
}

const SortableFloorplanItem = sortable(FloorplanItemS);

export const initFloorplanOptions = tour => {
  return produce(tour, draft => {
    draft.data.floorplans ??= [];
    draft.data.options ??= {};
    draft.data.options.floorplans ??= {
      enable3D: false,
      stacks: [{ start: 0 }],
    };
  });
};

class Floorplans extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectingPlan: false,
      currentPlan: 0,
      visible: true,
    };

    this.onSortItems = this.onSortItems.bind(this);
    this.onSelectPlan = this.onSelectPlan.bind(this);
    this.onDeleteFloorplan = this.onDeleteFloorplan.bind(this);
    this.onClickVisibleIcon = this.onClickVisibleIcon.bind(this);
  }

  onSelectPlan(plan) {
    this.setState({ selectingPlan: false });

    this.props.onUpdateTour(
      produce(initFloorplanOptions(this.props.tour), draft => {
        draft.data.floorplans.push({ ...plan, uuid: uuid(), hotspots: [] });
        draft.data.options.floorplans.stacks[0].floors = [
          ...draft.data.floorplans,
        ]
          .reverse()
          .map(({ uuid, name }) => ({
            uuid,
            name,
            scale: 1.5,
            model: { enabled: false },
          }));
      }),
    );
  }

  onDeleteFloorplan(uuid) {
    this.props.onUpdateTour(
      produce(initFloorplanOptions(this.props.tour), draft => {
        const index = draft.data.floorplans.findIndex(
          item => item.uuid === uuid,
        );
        if (index !== -1) draft.data.floorplans.splice(index, 1);
        draft.data.options.floorplans.stacks[0].floors = [
          ...draft.data.floorplans,
        ]
          .reverse()
          .map(({ uuid, name }) => ({
            uuid,
            name,
            scale: 1.5,
            model: { enabled: false },
          }));
      }),
    );
  }

  onSortItems = items => {
    this.props.onUpdateTour(
      produce(initFloorplanOptions(this.props.tour), draft => {
        draft.data.floorplans = items;
        draft.data.options.floorplans.stacks[0].floors = [
          ...draft.data.floorplans,
        ]
          .reverse()
          .map(({ uuid, name }) => ({
            uuid,
            name,
            scale: 1.5,
            model: { enabled: false },
          }));
      }),
    );
  };

  onClickVisibleIcon() {
    this.setState({ visible: !this.state.visible });
  }

  render() {
    const {
      classes,
      tour,
      onUpdateTour,
      onEmbedTour,
      onSaveTour,
      onTourSettings,
      dirty,
      onExit,
    } = this.props;

    const { selectingPlan, currentPlan } = this.state;

    const floorplans = tour.data.floorplans ?? [];

    return (
      <div className={classes.container}>
        <FloorplanSelector
          onSelect={this.onSelectPlan}
          show={selectingPlan}
          onClose={() => this.setState({ selectingPlan: false })}
          tourInfo={{ account: tour.account, team: tour.team }}
        />
        <TopBar
          links={{ onTourSettings, onEmbedTour, onSaveTour, dirty, onExit }}
        />
        <div className={classes.mainContainer}>
          {this.state.visible ? (
            <div className={classes.sidebar}>
              <FontAwesomeIcon
                icon={["fal", "times"]}
                className={classes.icon}
                fixedWidth
                onClick={this.onClickVisibleIcon}
              />
              <div className={classes.floorplans}>
                <ul
                  className={clsx(
                    "sortable-list",
                    classes.floorplanItemDraggable,
                  )}
                >
                  {[...floorplans].map((item, index) => (
                    <SortableFloorplanItem
                      key={index}
                      onSortItems={this.onSortItems}
                      items={[...floorplans]}
                      sortId={index}
                    >
                      <FloorplanItem
                        name={item.name}
                        uuid={item.uuid}
                        url={item.url}
                        active={index === currentPlan}
                        onClick={() => this.setState({ currentPlan: index })}
                        onDeleteFloorplan={this.onDeleteFloorplan}
                      />
                    </SortableFloorplanItem>
                  ))}
                </ul>
              </div>
              <div className={classes.buttonContainer}>
                <Button onClick={() => this.setState({ selectingPlan: true })}>
                  Add Floor Plan
                </Button>
              </div>
            </div>
          ) : (
            <FontAwesomeIcon
              icon={["fal", "bars"]}
              className={clsx(classes.icon, classes.close)}
              fixedWidth
              onClick={this.onClickVisibleIcon}
            />
          )}

          {floorplans[currentPlan] !== undefined && (
            <Floorplan
              images={Object.keys(tour.data.images).map(
                k => tour.data.images[k],
              )}
              floorplan={floorplans[currentPlan]}
              onUpdateTour={onUpdateTour}
              tour={tour}
            />
          )}
          <TourInformation
            title={tour.title}
            token={tour.token}
            updatedAt={tour.updated_at ?? null}
            onUpdateTitle={title => onUpdateTour({ ...tour, title })}
          />
        </div>
        <ModeSelect />
      </div>
    );
  }
}

const styles = theme => ({
  container: {
    width: "100%",
    height: "100%",
    "min-height": "100vh",
    "max-height": "100vh",
    display: "flex",
    "flex-direction": "column",
  },
  mainContainer: {
    position: "relative",
    display: "flex",
    flex: 1,
    overflow: "hidden",
    "background-color": "#333",
    height: "100%",
  },
  sidebar: {
    display: "flex",
    flex: 0,
    "background-color": theme.colorDarkGrey,
    "box-shadow": "2px 0px 5px rgba(0, 0, 0, 0.2)",
    color: "white",
    padding: `${theme.spacing3} ${theme.spacing2}`,
    width: "200px",
    "min-width": "200px",
    "flex-direction": "column",
  },
  floorplans: {
    flex: 1,
    "overflow-y": "auto",
  },
  buttonContainer: {
    flex: 0,
  },
  floorplanItemDraggable: {
    "list-style": "none",
    padding: 0,
  },
  icon: {
    "font-size": "25px",
    color: theme.textColor,
    cursor: "pointer",
  },
  close: {
    padding: `${theme.spacing2}`,
  },
});

export default injectSheet(styles)(Floorplans);
